import { Button, Dialog } from '@mui/material'
import React, { useEffect, useState } from 'react'
import colorConfigs from '../../../configs/colorConfigs'
import { useTheme } from '../../../components/hooks/useTheme'
import useConfirm from '../../../components/hooks/useConfirm'
import Cookies from 'js-cookie'
import useTenant from '../../../components/hooks/useTenant'
import { cancelReservation } from '../../../services/rental'
import useAuth from '../../../components/hooks/useAuth'
import { validateMin } from '../../../utils/validateMin'
import CustomFormatDatePicker from '../../../components/common/CustomFormatDatePicker'

type Props = {
    open: boolean;
    onClose: () => void;    
    preferredDateFormat: string;
    reservationId: number;
    summaryData: any;
    refreshTheReservation: () => void;
    setPleaseWait: (value: boolean) => void;
}

const CancelReservationPopup = ({
    open,
    onClose,   
    preferredDateFormat,
    reservationId,
    summaryData,
    refreshTheReservation,
    setPleaseWait,  
}: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const [cancellationDate, setCancellationDate] = useState<string>("");
    const [cancellationReason, setCancellationReason] = useState<string>("");
    const [totalPaidAmount, setTotalPaidAmount] = useState<number>(0);
    const [cancellationFee, setCancellationFee] = useState<number>(0);
  
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (open) {
            const totalPaid = summaryData?.totalPaid ? (summaryData?.totalPaid - summaryData?.totalRefunded) : 0;
            setTotalPaidAmount(totalPaid);

            const cancelledFee = totalPaid > 0 ? 0.10 * totalPaid : 0;
            setCancellationFee(Number(cancelledFee.toFixed(2)));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, summaryData])

    const resetForm = () => {
        onClose();
        setCancellationDate("");
        setCancellationReason("");
        setTotalPaidAmount(0);
        setCancellationFee(0);
    }

    const handleSendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await setConfirmData({
            title: 'Confirm Cancelled',
            summary: "Are you sure you want to Cancelled the reservation?",
            confirmBtn: 'Yes',
            isConfirm: async (callback: any) => {
                callback();

                if (cancellationFee > totalPaidAmount) {
                    setConfirmData({
                        title: 'Warning',
                        summary: "Cancellation Fee cannot exceed the original payment amount.",
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    return;
                }

                setPleaseWait(true);

                cancelReservation(
                        {
                            id: reservationId,
                            cancellationDate: cancellationDate,
                            cancellationFee: cancellationFee,
                            cancellationReason: cancellationReason

                        }, auth?.id, tenantId
                    )
                        .then(() => {
                            refreshTheReservation();
                            resetForm();
                            handleTenantError(null);
                        })
                        .catch((error) => {
                            setPleaseWait(false);
                            handleTenantError(error);
                        });
            }
        });

        setConfirmPopup(true);
    }

    return (
        <>
            <Dialog
                open={open}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                        overflowY: "unset"
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => resetForm()}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>

                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Cancel Reservation</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <form onSubmit={handleSendEmail}>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                <div className="col-span-1">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Cancellation Date</label>
                                                    <CustomFormatDatePicker
                                                        value={cancellationDate}
                                                        onChange={setCancellationDate}
                                                        dateFormat={preferredDateFormat}
                                                        required={true}
                                                    />
                                                </div>

                                                <div className="col-span-1">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                                                    <input
                                                        type="number"
                                                        step={0.01}
                                                        min={0}
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={cancellationFee}
                                                        onChange={(e) => setCancellationFee(validateMin(e.target.value, 0))}
                                                        required
                                                    />
                                                </div>

                                                <div className="col-span-2 relative">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Comments</label>
                                                    <textarea
                                                        className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        rows={6}
                                                        value={cancellationReason}
                                                        onChange={(e) => setCancellationReason(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-[2rem]">
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={resetForm}
                                                >
                                                    Cancel
                                                </Button>

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    type="submit"
                                                >
                                                    Cancel Reservation
                                                </Button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default CancelReservationPopup